// library
import _ from "lodash";
import L from "leaflet";
import { GeoJSON } from "react-leaflet";
import { LatLng } from "leaflet";
import { FeatureCollection, Feature } from "geojson";
import dayjs from "dayjs";
// type
import { Event } from "../types/event";

// プラグインが必要
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
// プラグインが必要
import ja from 'dayjs/locale/ja';
dayjs.locale(ja);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);

export default class Utils {

    /**
     * 本日日付を返却
     * 
     * @returns YYYY-MM-DD形式で日付を返す
     */
    static getDate = (): string => {
        return dayjs().format('YYYY-MM-DD');
    }

    /**
     * パラメータの日付分プラスを返却
     * 
     * @param increment
     * @returns YYYY-MM-DD形式で日付を返す
     */
    static getIncrementDate = (increment: number): string => {
        const today = dayjs();  // 現在の日付
        return today.add(increment, 'day').format('YYYY-MM-DD');
    }

    /**
     * イベントの日付表示文字列
     * 
     * @param startDate 
     * @param endDate 
     * @returns 
     */
    static eventDateString = (startDate?: string, endDate?: string): string => {

        if (!startDate || !endDate) {
            return "";
        }

        const start = dayjs(startDate);
        const startYear = start.format('YYYY');
        const startMonth = start.format('MM');
        const startDay = start.format('DD');
        const startWeek = start.format('dd');
        const nextDay = start.add(1, 'd');
        const end = dayjs(endDate);
        const endYear = end.format('YYYY');
        const endMonth = end.format('MM');
        const endDay = end.format('DD');
        const endWeek = end.format('dd');

        if (start.isSame(end)) {
            return `${startYear}年${startMonth}月${startDay}日(${startWeek})`;
        }

        if (nextDay.isSame(end)) {
            return `${startYear}年${startMonth}月${startDay}日(${startWeek})・${endDay}日(${endWeek})`;
        }

        if (startYear !== endYear) {
            return `${startYear}年${startMonth}月${startDay}日～${endYear}年${endMonth}月${endDay}日`;
        }

        if (startMonth !== endMonth) {
            return `${startYear}年${startMonth}月${startDay}日～${endMonth}月${endDay}日`;
        }

        return `${startYear}年${startMonth}月${startDay}日(${startWeek})～${endDay}日(${endWeek})`;
    }

    /**
     * イベントのパターン分け
     * 
     * @param startDate 
     * @param endDate 
     * @returns 
     */
    static eventDatePattern(startDate: string, endDate: string) : number | undefined {

        const today = dayjs();
        const start = dayjs(startDate);
        const startOfStart = dayjs(startDate).startOf('day');
        const endOfEnd = dayjs(endDate).endOf('day');
        const startOfEnd = dayjs(endDate).startOf('day');
        const oneWeek = dayjs().add(1, 'w');
        const oneMonth = dayjs().add(1, 'M');

        // 終了日が今日より前(消去)
        if (endOfEnd.isBefore(today)) {
            return;
        }

        // 開始日が1ヶ月より後(Month)
        if (start.isAfter(oneMonth)) {
            return 3;
        }

        // 開始日が1週間より後(Week)
        if (start.isAfter(oneWeek)) {
            return 2;
        }

        // 開始日が今日より前(実施中)
        if (startOfStart.isBefore(today)) {
            return 0;
        }

        // 直近開始前
        return 1; 
    }

    /**
     * 引数のアイコンでマーカー生成
     * 
     * @param latLng 
     * @param iconName 
     * @param className 
     * @returns 
     */
    static createIcon = (latLng: LatLng, iconName: string, className?: string) => {
        const icon = L.icon({
            iconUrl: `icons/${iconName}`,
            iconRetinaUrl: '',
            iconSize: [50, 50],
            iconAnchor: [12, 41],
            className: className
        })
        return L.marker(latLng, { icon });
    }

    /**
     * アイコンクリック時の関数
     * 
     * @param layer 
     * @param onClick 
     * @returns 
     */
    static createClickEvent = (layer: any, onClick: any) => layer.on({ clickMarker: onClick });

    /**
     * GeoJSONのタグ生成
     * 
     * @param key 
     * @param featureCollection 
     * @param pointToLayer 
     * @param onEachFeature 
     * @param style 
     * @param filter 
     * @returns 
     */
    static createGeoJson = (key: string, featureCollection: FeatureCollection, pointToLayer?: any, onEachFeature?: any, style?: any, filter?: any) => (
        <GeoJSON 
            key={key}
            data={featureCollection}
            pointToLayer={pointToLayer}
            onEachFeature={onEachFeature}
            style={style}
            filter={filter}
        />
    );

    /**
     * GeojsonのFeature生成
     * 
     * @param events 
     * @returns 
     */
    static createFeatures = (events: Event[]) => {
        const features: any[] = _.map(events, (event: Event) => {
            const newEvent = _.cloneDeep(event);
            delete newEvent.geometry;
            return {
                type: "Feature",
                geometry: event.geometry,
                properties: newEvent
            }
        })
        return features;
    }

    /**
     * 点滅判定
     * 
     * @param event 
     * @returns 
     */
    static isBlanking = (event: Event): boolean => {
        const start = dayjs(`${event.startDate}`).startOf('day');
        const end = dayjs(`${event.endDate}`).endOf('day');
        const yyyyMmDd = this.getDate();
        const todayStart = dayjs(`${yyyyMmDd} ${event.startTime}`);
        const todayEnd = dayjs(`${yyyyMmDd} ${event.endTime}`);
        const now = dayjs();

        // 開始時間・終了時間がない場合はfalse
        if (!event?.startTime || !event?.endTime) {
            return false;
        }

        // 今日が開始日と終了日の間に存在
        if (now.isAfter(start) && now.isBefore(end)) {
            // 今日の時間の間
            if (now.isAfter(todayStart) && now.isBefore(todayEnd)) {
                return true;
            }
            return false;
        }
        return false;
    }

    /**
     * リクエスト時に遅延させる
     * @param millisecond 
     * @returns 
     */
    static asyncSleep(millisecond: number) {
        return new Promise(onSuccess => {
            setTimeout(() => {
                onSuccess(500);
            }, millisecond);
        });
    }

    /**
     * muniCdからprefectureを抽出
     * @param miniCd 
     * @returns 
     */
    static muniCdToPrefecture(miniCd: string): number {
        if (!miniCd) {
            return 0;
        }

        if (miniCd.length === 4) {
            return Number(miniCd.substring(0,1));
        } 

        if (miniCd.length === 5) {
            return Number(miniCd.substring(0,2));
        }

        return 0;
    }

    /**
     * lat lng正当性確認
     * @param event 
     */
    static latLngLegitimacy(event: Event) {
        if (event.geometry?.type === "Point") {
            const lat = event.geometry?.coordinates[0];
            const lng = event.geometry?.coordinates[1];
            if (lat < 150 && lat > 120) {
                event.geometry.coordinates = [lat, lng];
            } else {
                event.geometry.coordinates = [lng, lat]
            }
        }
    }

    /**
     * 終わりの日付が今日より後か
     * @param endDate 
     * @returns 
     */
    static beforeEvent(endDate: string): boolean {
        const today = dayjs();
        const end = dayjs(endDate).endOf('day');
        // 終了日が今日より前(消去)
        if (today.isBefore(end)) {
            return true;
        }
        return false;
    }
}